import { DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { GridItemChangeEvent } from '@progress/kendo-react-grid';
import { isEmpty } from 'lodash';
import { FC, useCallback, useMemo, useState } from 'react';
import uuid from 'react-uuid';
import { SplitLineItemColumns } from 'src/assets/static/invoice_details/SplitLineItemColumns';
import GridWrapper from 'src/components/GridWrapper';

import PopUpContentWrapper from 'src/components/PopUpContentWrapper';
import Button, { isButtonDisabled } from 'src/components/design-system/Button';
import Dialog from 'src/components/design-system/Dialog';
import PopUp from 'src/components/design-system/PopUp';
import { SplitReceiptLineItem } from 'src/constants/permissionsList';
import useConfirm from 'src/hooks/useConfirm';
import usePermission from 'src/hooks/usePermissions';
import { InvoiceLineItem } from 'src/services/useInvoiceLineItemsApi';
import { useContextSelector } from 'use-context-selector';
import { InvoiceContext } from './InvoiceDetailsContext';
import useInvoiceButtonState from './useInvoiceButtonState';

interface ModalData {
  title: string;
  content: string;
}

const SplitLineItems = () => {
  const throttledInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.throttledInvoiceLineItems
  );
  const setInvoiceLineItems = useContextSelector(
    InvoiceContext,
    (value) => value.setInvoiceLineItems
  );
  const splitLineItem = useContextSelector(InvoiceContext, (value) => value.splitLineItem);
  const setSplitLineItem = useContextSelector(InvoiceContext, (value) => value.setSplitLineItem);
  const splitOption = useContextSelector(InvoiceContext, (value) => value.splitOption);
  const totalAmount = useContextSelector(InvoiceContext, (value) => value.totalAmount);
  const isInvoiceInProgress = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceInProgress
  );

  const isInvoiceEditDisabled = useContextSelector(
    InvoiceContext,
    (value) => value.isInvoiceEditDisabled
  );
  const selectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.selectedLineItemIds
  );
  const setSelectedLineItemIds = useContextSelector(
    InvoiceContext,
    (value) => value.setSelectedLineItemIds
  );

  const [disableMessage, buttonState] = useInvoiceButtonState('SplitLineItem');

  const { confirm } = useConfirm();
  const hasPermissionToSplit = usePermission({ permissions: SplitReceiptLineItem });
  const [openSplitDialog, setOpenSplitDialog] = useState(false);

  const {
    isSingleLineItemSelected,
    isMultipleLineItemsSelected,
    isSelected,
    isZeroQuantity,
    totalQuantity,
    selectedLineItems,
  } = useMemo(() => {
    const selectedLineItems = throttledInvoiceLineItems?.filter((lineItem) =>
      selectedLineItemIds.includes(lineItem.receiptLineItemId)
    );
    const totalQuantity = Number(selectedLineItems[0]?.quantityReceived);
    return {
      selectedLineItems,
      isSelected: !!selectedLineItems?.length,
      isSingleLineItemSelected: selectedLineItems?.length === 1,
      isMultipleLineItemsSelected: selectedLineItems?.length > 1,
      isZeroQuantity: totalQuantity === 0,
      totalQuantity,
    };
  }, [throttledInvoiceLineItems, selectedLineItemIds]);

  const disableSplitButton = useMemo(
    () =>
      !(hasPermissionToSplit && isSelected) ||
      !isInvoiceInProgress ||
      selectedLineItems[0]?.isNewlyAddedLineItemNow ||
      isInvoiceEditDisabled,
    [
      hasPermissionToSplit,
      isInvoiceEditDisabled,
      isInvoiceInProgress,
      isSelected,
      selectedLineItems,
    ]
  );

  const handleMultipleSelection = useCallback(async () => {
    await confirm({
      title: 'Cannot Split Line Items',
      content: (
        <div className="font-bold text-sm">
          You have selected multiple Line Items. Only 1 Line Item can be split at a time.
        </div>
      ),
      cancelButtonText: 'OK',
      cancelColor: 'info',
      hasNoConfirmButton: true,
      width: '500px',
      actionBarStyles: 'flex justify-end',
    });
  }, [confirm]);

  const handleZeroQuantity = useCallback(async () => {
    await confirm({
      title: 'Split Zero Quantity',
      content: (
        <div className="font-bold text-sm">
          This line item has a quantity of zero and cannot be split.
        </div>
      ),
      cancelButtonText: 'OK',
      cancelColor: 'info',
      hasNoConfirmButton: true,
      width: '500px',
      actionBarStyles: 'flex justify-end',
    });
  }, [confirm]);

  const handleSplitError = useCallback(
    async (data: ModalData) => {
      await confirm({
        title: data?.title,
        content: <div className="font-bold text-sm">{data?.content}</div>,
        cancelButtonText: 'OK',
        cancelColor: 'info',
        hasNoConfirmButton: true,
        width: '500px',
        actionBarStyles: 'flex justify-end',
      });
    },
    [confirm]
  );

  const handleClick = useCallback(() => {
    if (isMultipleLineItemsSelected) {
      handleMultipleSelection();
      return;
    } else if (isZeroQuantity) {
      handleZeroQuantity();
      return;
    } else if (isSingleLineItemSelected) {
      setOpenSplitDialog(true);
      setSplitLineItem([
        ...selectedLineItems.map((item) => ({ ...item, quantityReceived: 0, selected: false })),
        {
          canDelete: 0,
          isTaxable: true,
          description: selectedLineItems?.[0]?.description,
          glCd: '',
          poQty: 0,
          quantityReceived: 0,
          priceAmt: selectedLineItems?.[0]?.priceAmt,
          uom: selectedLineItems?.[0]?.uom,
          rowId: uuid(),
          subTotal: 0,
          productItemNum: selectedLineItems?.[0]?.productItemNum,
          isNewLineItem: true,
        } as unknown as InvoiceLineItem,
      ]);
    }
  }, [
    isMultipleLineItemsSelected,
    isZeroQuantity,
    isSingleLineItemSelected,
    handleMultipleSelection,
    handleZeroQuantity,
    setSplitLineItem,
    selectedLineItems,
  ]);

  const handleAddNewLineItem = () => {
    setSplitLineItem((prev) => {
      const previousLineItem = [...prev];
      previousLineItem.push({
        canDelete: 0,
        isTaxable: true,
        description: selectedLineItems?.[0]?.description,
        glCd: '',
        poQty: 0,
        quantityReceived: 0,
        priceAmt: selectedLineItems?.[0]?.priceAmt,
        uom: selectedLineItems?.[0]?.uom,
        rowId: uuid(),
        subTotal: 0,
        productItemNum: selectedLineItems?.[0]?.productItemNum,
        isNewLineItem: true,
      } as unknown as InvoiceLineItem);
      return previousLineItem;
    });
  };

  const handleItemChange = useCallback(
    (e: GridItemChangeEvent) => {
      const { field = '', value } = e;
      const { rowId } = e.dataItem;

      setSplitLineItem((prev) => [
        ...prev.map((item) => ({
          ...item,
          ...(rowId === item.rowId && {
            [field]: value,
          }),
        })),
      ]);
    },
    [setSplitLineItem]
  );

  const validateSplitLineItemField = useCallback(() => {
    const isValid = [...splitLineItem].every(
      (item) =>
        !isEmpty(item.glCd) &&
        !(typeof item.quantityReceived == 'string' && isEmpty(item.quantityReceived))
    );
    const total = totalAmount('quantityReceived');
    let errorMessage: ModalData = {} as ModalData;
    let negativeItemIndex;
    const isOneItemNegative = [...splitLineItem].some((item, idx) => {
      negativeItemIndex = idx + 1;
      return Number(item.quantityReceived) < 0;
    });
    const isAllItemNegative = [...splitLineItem].every((item) => Number(item.quantityReceived) < 0);

    if (!isValid) {
      errorMessage = {
        title: 'Error',
        content:
          'Please make sure that every line has a GL Code and Amount assigned in order to Split.',
      };
      handleSplitError(errorMessage);
      return false;
    } else if (isOneItemNegative && !isAllItemNegative) {
      errorMessage = {
        title: 'Split Amount Error',
        content: `Split Item #${negativeItemIndex}  has an incorrect Amount. Amounts must either be all positive Amounts or all negative Amounts.`,
      };
      handleSplitError(errorMessage);
      return false;
    } else if (splitOption === 'Percentage') {
      if (total !== 100) {
        errorMessage = {
          title: 'Split Amount Error',
          content:
            'The Split Amount Total does not add up to 100%. The total must be 100% in order to split the Line Item.',
        };
        handleSplitError(errorMessage);
        return false;
      }
    } else if (splitOption === 'Quantity') {
      if (total !== totalQuantity) {
        errorMessage = {
          title: 'Split Amount Error',
          content: `The Split Amount Total (${total}) does not add up to the current Line Item Total (${totalQuantity}). The totals must match in order to Split the Line Item.`,
        };
        handleSplitError(errorMessage);
        return false;
      }
    }
    return true;
  }, [splitLineItem, splitOption, totalQuantity, totalAmount, handleSplitError]);

  const addLineItem = useCallback(() => {
    const newLineItems = splitLineItem.map((item) => {
      const newQuantity =
        splitOption === 'Percentage'
          ? totalQuantity * (Number(item.quantityReceived) / 100)
          : Number(item.quantityReceived);
      return {
        ...item,
        quantityReceived: newQuantity,
        subTotal: (item?.priceAmt || 0) * newQuantity,
      };
    });
    setInvoiceLineItems((prev) => {
      const previousLineItem = [...prev];

      return [
        ...previousLineItem.filter((item) => !selectedLineItemIds.includes(item.receiptLineItemId)),
        ...newLineItems,
      ];
    });
  }, [selectedLineItemIds, setInvoiceLineItems, splitLineItem, splitOption, totalQuantity]);

  const handleSplitLineItem = useCallback(() => {
    if (validateSplitLineItemField()) {
      addLineItem();
      setOpenSplitDialog(false);
      setSelectedLineItemIds([]);
    }
  }, [validateSplitLineItemField, addLineItem, setSelectedLineItemIds]);

  const calculateTotalAmount = () => {
    const total = totalAmount('quantityReceived');
    if (splitOption === 'Percentage') {
      return `${total.toFixed(2)}% of 100% assigned`;
    } else {
      return `${total.toFixed(2)} of ${totalQuantity.toFixed(2)} assigned`;
    }
  };
  const messages = useMemo(() => {
    let messages: string[] = [];
    const lineItemCount = selectedLineItems.length;
    const isTwoLineItemsSelected = lineItemCount >= 1;

    if (isInvoiceEditDisabled) {
      messages = ['Current document is not in progress'];
    } else if (selectedLineItems[0]?.isNewlyAddedLineItemNow) {
      messages = ['Please save the newly added Line Item on the document'];
    } else if (!isTwoLineItemsSelected) {
      messages = ['Please select 1 Line Item to Split.'];
    } else {
      messages = [];
    }

    return messages;
  }, [isInvoiceEditDisabled, selectedLineItems]);

  return (
    <>
      <PopUp
        content={
          <div className="m-1 max-w-[300px]">
            <PopUpContentWrapper
              type="Information"
              contents={[...(isButtonDisabled(buttonState) ? disableMessage : messages)]}
            />
          </div>
        }
        show={!!messages.length || isButtonDisabled(buttonState)}
      >
        <Button onClick={handleClick} disabled={disableSplitButton} buttonState={buttonState}>
          Split Line Item
        </Button>
      </PopUp>
      <Dialog
        title="Split Line Items"
        open={openSplitDialog}
        onClose={() => setOpenSplitDialog(false)}
        width={700}
        id="receipt-wizard"
      >
        <div>
          <GridWrapper
            resizable
            columns={SplitLineItemColumns}
            dataSource={splitLineItem}
            total={SplitLineItemColumns.length}
            onItemChange={handleItemChange}
            gridToolbarChildren={<SplitLineItemToolbar addNewLineItem={handleAddNewLineItem} />}
            DataItemKey="rowId"
            pageable={false}
            tableTextId="lineItems"
          />
        </div>
        <DialogActionsBar>
          <div className="flex justify-end items-center">
            <span className="font-bold text-sm mr-2">{calculateTotalAmount()}</span>
            <Button onClick={handleSplitLineItem} color="info" className="mr-2">
              Split
            </Button>
            <Button onClick={() => setOpenSplitDialog(false)} color="info">
              Cancel
            </Button>
          </div>
        </DialogActionsBar>
      </Dialog>
    </>
  );
};

export default SplitLineItems;

interface SplitLineItemToolbarProps {
  addNewLineItem: () => void;
}

const option = ['Percentage', 'Quantity'];

const SplitLineItemToolbar: FC<SplitLineItemToolbarProps> = ({ addNewLineItem }) => {
  const splitOption = useContextSelector(InvoiceContext, (value) => value.splitOption);
  const setSplitOption = useContextSelector(InvoiceContext, (value) => value.setSplitOption);

  const handleChange = useCallback(
    (e: DropDownListChangeEvent) => {
      setSplitOption(e.target.value);
    },
    [setSplitOption]
  );

  return (
    <div className="flex items-center justify-between w-full">
      <Button onClick={addNewLineItem}>Add New Line</Button>
      <div>
        <span className="text-white">Split By: </span>
        <DropDownList
          id="view-drop-down-list"
          style={{ width: 150 }}
          data={option}
          value={splitOption}
          onChange={handleChange}
        />
      </div>
    </div>
  );
};
